import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useInView } from "@/hooks/useInView";

interface LatestPost {
  slug: string;
  title: string;
  featured_image: string | null;
  created_at: string;
  content: string;
}

function toThumbnail(url: string, width = 300, height = 200): string {
  if (url.includes("/storage/v1/object/public/")) {
    return (
      url.replace("/storage/v1/object/public/", "/storage/v1/render/image/public/") +
      `?width=${width}&height=${height}&resize=cover&quality=75`
    );
  }
  return url;
}

function calcReadingTime(content: string): number {
  const text = content.replace(/<[^>]*>/g, " ");
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

function LatestCard({ post, index }: { post: LatestPost; index: number }) {
  const { ref, isInView } = useInView<HTMLDivElement>({ threshold: 0.2 });
  const [imgLoaded, setImgLoaded] = useState(false);
  const onLoad = useCallback(() => setImgLoaded(true), []);
  const readingTime = calcReadingTime(post.content || "");

  return (
    <div
      ref={ref}
      className={cn(
        "transition-all duration-700",
        isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <Link
        to={`/blog/${post.slug}`}
        className="flex flex-col glass-card rounded-2xl hover:border-primary/20 transition-all duration-300 group overflow-hidden h-full"
      >
        {post.featured_image ? (
          <div className="overflow-hidden bg-muted w-full relative h-28 sm:h-32">
            {!imgLoaded && <div className="absolute inset-0 bg-muted animate-pulse" />}
            <img
              src={toThumbnail(post.featured_image)}
              alt={post.title}
              className={cn(
                "w-full h-28 sm:h-32 object-cover transition-all duration-500 group-hover:scale-105",
                imgLoaded ? "opacity-100" : "opacity-0"
              )}
              loading="lazy"
              decoding="async"
              onLoad={onLoad}
            />
          </div>
        ) : (
          <div className="bg-gradient-hero h-2" />
        )}
        <div className="flex flex-col flex-1 p-3 sm:p-4">
          <h3 className="font-display font-bold text-foreground group-hover:text-primary transition-colors text-sm leading-snug mb-1">
            {post.title}
          </h3>
          <div className="text-xs text-muted-foreground mt-auto pt-2 flex items-center gap-2">
            <time dateTime={post.created_at}>
              {new Date(post.created_at).toLocaleDateString("de-DE", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })}
            </time>
            <span aria-hidden="true">·</span>
            <span>{readingTime} Min. Lesezeit</span>
          </div>
        </div>
      </Link>
    </div>
  );
}

export function LatestBlogPosts() {
  const { data: posts } = useQuery<LatestPost[]>({
    queryKey: ["latest-blog-posts-home"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("slug, title, featured_image, created_at, content")
        .eq("published", true)
        .order("created_at", { ascending: false })
        .limit(4);
      if (error) throw error;
      return (data ?? []) as LatestPost[];
    },
    staleTime: 30 * 60 * 1000,
  });

  if (!posts || posts.length === 0) return null;

  return (
    <section className="py-12 sm:py-16 lg:py-20">
      <div className="container-section">
        <div className="flex items-end justify-between gap-4 mb-8">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
            Neueste Artikel aus dem Blog
          </h2>
          <Link
            to="/blog"
            className="text-sm text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
          >
            Alle ansehen →
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {posts.map((post, index) => (
            <LatestCard key={post.slug} post={post} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}